import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Badge, Empty, Input, Space, Spin, Tooltip, Typography } from 'antd';
import cx from 'classnames';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { ReactComponent as GridViewIcon } from '../../../assets/svgs/grid-view-icon.svg';
import { ReactComponent as TableViewIcon } from '../../../assets/svgs/list-view-icon.svg';
import { useListFetcher } from '../../../hooks/useListFetcher';
import { useSyncedSearch } from '../../../hooks/useSyncedSearch';
import { useUrlHydration } from '../../../hooks/useUrlHydration';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  hydrateFromUrl,
  paginatePlanograms,
  setSearchQuery,
} from '../../../store/features/planogram/planogramSlice';
import { ListApiConfig } from '../../../types/list';
import { UserPermissions } from '../../../util';
import { buildListParams } from '../../../util/listApiParams';
import { DrawerHashRoute } from '../../containers/Drawers/types';
import Button from '../../elements/Button';
import NoResultsFound from '../../elements/NoResultsFound';
import ViewWrapper from '../../elements/ViewWrapper';

import { useAuth } from '../../../hooks/useAuth';
import NoPermission from '../../elements/NoPermission';
import './PlanogramProjectsView.less';
import PlanogramProjectItemsList, {
  PlanogramItemsListViewType,
} from './components/PlanogramProjectItemsList/PlanogramProjectItemsList';

const { Title } = Typography;

const antSpinIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;
const planogram2ListViewTypeKey = 'planogram-list-view-type';
const lastPlanogram2ProjectsItemLengthKey =
  'last-planogram-projects-item-length-key';

const PlanogramProjectsView = () => {
  const { hasPermission } = useAuth();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const [clearingSearch, setClearingSearch] = useState(false);
  const [viewType, setViewType] = useState<PlanogramItemsListViewType>(
    (localStorage.getItem(
      planogram2ListViewTypeKey
    ) as PlanogramItemsListViewType) || PlanogramItemsListViewType.Grid
  );
  const [pagination, setPagination] = useState({ offset: 0, limit: 20 });

  const {
    value: projects,
    fetchingPlanograms,
    searchingPlanograms,
    totalCount,
    page,
    pageSize,
    search: storeSearch,
    sort,
  } = useAppSelector((state) => state.planogram);

  const listApiConfig: ListApiConfig = useMemo(
    () => ({
      defaultSort: 'updated_at:desc',
      columns: 'createdByUser.*,client.*,media_files.*',
      searchParamKey: 'name',
      searchTransformer: (s) => `*${s.toLowerCase()}*`,
    }),
    []
  );

  const hasNoSearchResultsFound = useMemo(
    () => !fetchingPlanograms && !!storeSearch && projects.length === 0,
    [fetchingPlanograms, storeSearch, projects.length]
  );

  const hasProjects = !(!fetchingPlanograms && projects.length === 0);

  const hasNoProjects = useMemo(
    () => !fetchingPlanograms && projects.length === 0 && !clearingSearch,
    [fetchingPlanograms, projects.length, clearingSearch]
  );

  const hasMoreProjects =
    hasProjects && pagination?.offset! + pagination?.limit! < totalCount!;

  const isPermittedToLoadMore =
    !fetchingPlanograms &&
    !searchingPlanograms &&
    !storeSearch &&
    hasMoreProjects;

  // initial load handled by useUrlHydration/useListFetcher

  // URL hydration and centralized fetching for Table mode; Grid remains infinite-load
  const { hasHydrated } = useUrlHydration({
    location,
    controlsFromStore: { page, pageSize, search: storeSearch, sort },
    onHydrate: (urlControls) => {
      // Only allow server-supported sort fields
      const next: any = { ...urlControls };
      if (next.sort) {
        const field = String(next.sort).split(':')[0];
        const allowed = new Set(['updated_at', 'name']);
        if (!allowed.has(field)) delete next.sort;
      }
      dispatch(hydrateFromUrl(next));
    },
  });

  const fetchThunk = useCallback(
    (params: Record<string, any>) => dispatch(paginatePlanograms(params)),
    [dispatch]
  );

  useListFetcher({
    hasHydrated,
    controlsFromStore: { page, pageSize, search: storeSearch, sort },
    config: listApiConfig,
    fetchThunk,
  });

  const createNewProjectButton = useMemo(
    (): JSX.Element => (
      <Link to={DrawerHashRoute.PlanogramForm}>
        <Button type="primary" data-cy="create-new-planogram-project-btn">
          Create New Project
          <PlusOutlined />
        </Button>
      </Link>
    ),
    []
  );

  const uploadRSSegmentButton = useMemo(
    (): JSX.Element => (
      <Link to={DrawerHashRoute.RSSegmentUploadForm}>
        <Button type="primary" data-cy="upload-rs-segment-btn">
          Upload RS Segment
          <UploadOutlined />
        </Button>
      </Link>
    ),
    []
  );

  const onSearch = useCallback(
    (value: string) => {
      setClearingSearch(value === '');
      const params = buildListParams(
        { page: 1, pageSize, search: value, sort },
        listApiConfig
      );
      return dispatch(paginatePlanograms(params));
    },
    [dispatch, pageSize, sort, listApiConfig]
  );

  const { searchValue, onChange, clear } = useSyncedSearch({
    storeSearchValue: storeSearch,
    setStoreSearch: (v) => dispatch(setSearchQuery(v)),
    pageSize,
    sort,
    history,
    location,
    onSearch,
  });

  const updatePlanogram2ListViewType = useCallback(
    (type: PlanogramItemsListViewType) => {
      setViewType(type);
      localStorage.setItem(planogram2ListViewTypeKey, type);
    },
    []
  );

  const headerSectionRight = useMemo(
    (): JSX.Element => (
      <Space direction="horizontal" size="middle" align="center">
        <Tooltip title="Grid view" mouseEnterDelay={0.2} mouseLeaveDelay={0}>
          <div
            className={cx('planogram-view-type-icon', {
              selected: viewType === PlanogramItemsListViewType.Grid,
            })}
            onClick={() =>
              updatePlanogram2ListViewType(PlanogramItemsListViewType.Grid)
            }
          >
            <GridViewIcon color="#000" />
          </div>
        </Tooltip>
        <Tooltip title="Table view" mouseEnterDelay={0.2} mouseLeaveDelay={0}>
          <div
            className={cx('planogram-view-type-icon', {
              selected: viewType === PlanogramItemsListViewType.Table,
            })}
            onClick={() =>
              updatePlanogram2ListViewType(PlanogramItemsListViewType.Table)
            }
          >
            <TableViewIcon />
          </div>
        </Tooltip>
        <Input
          allowClear={!searchingPlanograms}
          value={searchValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search for a project..."
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
          suffix={searchingPlanograms && <Spin indicator={antSpinIcon} />}
        />
        {hasPermission(UserPermissions.PlanogramProjectsCreate) && (
          <>
            {uploadRSSegmentButton}
            {createNewProjectButton}
          </>
        )}
      </Space>
    ),
    [
      viewType,
      searchingPlanograms,
      searchValue,
      createNewProjectButton,
      uploadRSSegmentButton,
      updatePlanogram2ListViewType,
      onChange,
      hasPermission,
    ]
  );

  useEffect(() => {
    if (!!storeSearch || projects.length > 20) return;

    localStorage.setItem(
      lastPlanogram2ProjectsItemLengthKey,
      `${projects.length}`
    );
  }, [projects.length, storeSearch]);

  const handleProjectClick = (projectId: string) => {
    history.push(`/planogram/${projectId}`);
  };

  const renderContent = () => {
    if (searchingPlanograms) {
      return (
        <div className="custom-fields-view-container">
          <Spin indicator={antSpinIcon} />
        </div>
      );
    }

    if (!hasNoProjects) {
      return (
        <PlanogramProjectItemsList
          skeletonItemLength={
            +localStorage.getItem(
              lastPlanogram2ProjectsItemLengthKey
            )! as number
          }
          viewType={viewType}
          loading={fetchingPlanograms}
          projects={projects}
          onLoadMore={() => {
            setPagination((prev) => ({
              ...prev,
              offset: prev.offset + prev.limit,
            }));
          }}
          hasMore={isPermittedToLoadMore}
          onItemClick={handleProjectClick}
        />
      );
    }

    if (hasNoSearchResultsFound) {
      return <NoResultsFound searchQuery={searchValue} onClear={clear} />;
    }

    if (!hasMoreProjects) {
      return (
        <div className="custom-fields-view-container">
          <Empty
            description="Currently no planogram projects."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {createNewProjectButton}
          </Empty>
        </div>
      );
    }
  };

  // debounced search managed by useSyncedSearch

  if (!hasPermission(UserPermissions.PlanogramProjectsView)) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      headerTitle={
        <Space direction="horizontal">
          <Title level={3}>Projects</Title>
          <Badge
            size="default"
            count={totalCount}
            className="total-count-badge"
            overflowCount={999}
          />
        </Space>
      }
      headerSectionRight={headerSectionRight}
    >
      {renderContent()}
    </ViewWrapper>
  );
};

export default PlanogramProjectsView;
